import { observer } from "mobx-react";
import { useState } from "react";
import { Button, Modal, Steps } from "antd";
import { AppStore } from "~/stores/AppStore";
import { ClientsController } from "../../ClientsController";
import ModuleSelectionStep from "./steps/ModuleSelectionStep";
import GeneralSettingsStep from "./steps/GeneralSettingsStep";
import RouteViewConfigStep from "./steps/RouteViewConfigStep";
import DispatchViewConfigStep from "./steps/DispatchViewConfigStep";
import MobileApplicationStep from "./steps/MobileApplicationStep";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faBoxes, faCog, faRoute, faTruck, faMobileAlt } from "@fortawesome/free-solid-svg-icons";

const { Step } = Steps;

interface ClientWizardProps {
  store?: AppStore;
  controller?: ClientsController;
  visible: boolean;
  clientData: {
    clientRef: string;
    name: string;
    vertical: string;
    country: string;
    demo: boolean;
  };
  initialWizardData?: any; // For edit mode
  mode?: "create" | "edit"; // Mode flag
  onComplete: (wizardConfig: any) => void;
  onCancel: () => void;
}

const ClientWizard = observer(({ store, controller, visible, clientData, initialWizardData, mode = "create", onComplete, onCancel }: ClientWizardProps) => {
  const [currentStep, setCurrentStep] = useState(0);
  const [wizardData, setWizardData] = useState(
    initialWizardData || {
      // Page 1: Module Selection
      modules: {
        tactical: false,
        routeView: false,
        dispatch: false,
        mobileApp: false,
        geocoding: true,
        mapEditor: false,
        serviceDesk: false,
        jobCreationEdit: false,
        trackingPage: false,
        blackboxRouting: false,
      },
      // Blackbox Routing Service Config
      blackboxRouteService: {
        geocode: false,
      },
      // Page 2: General Settings
      generalSettings: {
        enableNodeAggregation: false,
        nodeAggregationCriteria: ["coordinates"],
        enableStopJobAggregation: false,
        stopJobAggregationCriteria: ["coordinates"],
        stopTimeComputationMode: "job",
        jobTimeComputationMode: "jobTotal",
      },
      // Page 3: Route View Config
      routeView: {
        routeViewImpactAssessmentEnabled: false,
        defaultLoadDay: "today",
      },
      // Page 4: Dispatch View Config
      dispatchView: {
        dispatchViewImpactAssessmentEnabled: false,
        allowAggregationOnRoutedNode: false,
        maxTripsAllowed: 5,
        enableCustomerPromiseTimeWindow: false,
        calculateCustomerPromiseTimeWindow: [],
        autoDispatchRoute: false,
        forceDispatchAllTrips: false,
        mandatoryTripViolationReason: false,
      },
      // Page 5: Mobile Application Config
      mobileApplication: {
        clientSettings: {
          allowManifestTakeover: false,
          franchiseePreInspection: false,
          jobPreInspection: false,
          jobPostInspection: false,
          franchiseePostInspection: false,
        },
        csRequestManifestEnabled: false,
        csRequestManifestChangeRequestDateEnabled: false,
        csAdHocVehicleInspectionEnabled: false,
        csContactsEnabled: false,
        csRouteManifestForceCompanyDriveExecuteActivitiesInSequence: false,
        csRouteManifestForceFranchiseeDriveExecuteActivitiesInSequence: false,
        csMultiTripRequestNextTripEnabled: false,
        csStopRouteEnabled: false,
        csActivityStopCancelJobBeforeArrivalEnabled: false,
        csActivityStopReAttemptCancelledJobsEnabled: false,
        csActivityStopSuspendOptionVisible: false,
        csGroupedJobsEnabled: false,
        csGroupedJobsRestrictGroupingOnlyToLoadedItems: false,
        csGroupedJobsRestrictGroupingOnlyToJobsOnTheSameNode: false,
        csVisitPlanJobItemOperationPartialQuantityChangeEnabled: false,
        csVisitPlanJobItemOperationPartialStatusEnabled: false,
        csVehicleLoadingBarcodeScanningIncreasesPartialQuantityLoaded: false,
        csAdHocDeliveryEnabled: false,
        csAdHocCollectionEnabled: false,
        csAdHocJobOpenVisitPlanAfterCreateCollection: false,
        csVisitPlanAdHocItemCreationEnabled: false,
        csAdHocJobItemSetOperationStatusToSuccessWhenCreate: false,
        csAdHocJobItemQuantityVisible: false,
      },
    }
  );

  const steps = [
    {
      title: "Modules",
      icon: <FontAwesomeIcon icon={faBoxes} />,
      content: (
        <ModuleSelectionStep
          data={wizardData.modules}
          onChange={(modules) => {
            // Update blackboxRouteService.geocode based on geocoding and blackboxRouting settings
            const blackboxRouteService = {
              geocode: modules.blackboxRouting && modules.geocoding,
            };
            setWizardData({ ...wizardData, modules, blackboxRouteService });
          }}
        />
      ),
    },
    {
      title: "General Settings",
      icon: <FontAwesomeIcon icon={faCog} />,
      content: <GeneralSettingsStep data={wizardData.generalSettings} modules={wizardData.modules} onChange={(generalSettings) => setWizardData({ ...wizardData, generalSettings })} />,
    },
    {
      title: "Route View",
      icon: <FontAwesomeIcon icon={faRoute} />,
      content: <RouteViewConfigStep data={wizardData.routeView} modules={wizardData.modules} onChange={(routeView) => setWizardData({ ...wizardData, routeView })} />,
    },
    {
      title: "Dispatch View",
      icon: <FontAwesomeIcon icon={faTruck} />,
      content: <DispatchViewConfigStep data={wizardData.dispatchView} modules={wizardData.modules} onChange={(dispatchView) => setWizardData({ ...wizardData, dispatchView })} />,
    },
    {
      title: "Mobile",
      icon: <FontAwesomeIcon icon={faMobileAlt} />,
      content: (
        <MobileApplicationStep
          data={wizardData.mobileApplication}
          modules={wizardData.modules}
          generalSettings={wizardData.generalSettings}
          onChange={(mobileApplication) => setWizardData({ ...wizardData, mobileApplication })}
        />
      ),
    },
  ];

  const next = () => {
    setCurrentStep(currentStep + 1);
  };

  const prev = () => {
    setCurrentStep(currentStep - 1);
  };

  const handleFinish = () => {
    onComplete(wizardData);
  };

  const handleCancel = () => {
    setCurrentStep(0);
    onCancel();
  };

  return (
    <Modal
      title={
        <div className="flex items-center">
          <span className="text-lg font-semibold">
            {mode === "edit" ? "Edit Client Configuration" : "Client Configuration Wizard"} - {clientData.name} ({clientData.clientRef})
          </span>
        </div>
      }
      open={visible}
      onCancel={handleCancel}
      width={1000}
      footer={null}
      centered
      maskClosable={false}
      styles={{ body: { paddingTop: 24 } }}
    >
      <div className="mb-8">
        <Steps current={currentStep} size="small">
          {steps.map((item) => (
            <Step key={item.title} title={item.title} icon={item.icon} />
          ))}
        </Steps>
      </div>

      <div className="min-h-[450px] max-h-[500px] overflow-y-auto px-2 py-4 bg-gray-50 rounded-lg border border-gray-200">{steps[currentStep].content}</div>

      <div className="flex justify-between mt-6 pt-4 border-t border-gray-200">
        <div>
          {currentStep > 0 && (
            <Button onClick={prev} size="large">
              Previous
            </Button>
          )}
        </div>
        <div className="space-x-2">
          <Button onClick={handleCancel} size="large">
            Cancel
          </Button>
          {currentStep < steps.length - 1 && (
            <Button type="primary" onClick={next} size="large">
              Next
            </Button>
          )}
          {currentStep === steps.length - 1 && (
            <Button type="primary" onClick={handleFinish} size="large">
              Finish
            </Button>
          )}
        </div>
      </div>
    </Modal>
  );
});

export default ClientWizard;
